export type ProductStatus = 'draft' | 'published' | 'paused' | 'archived';
export type OfferCondition = 'new' | 'refurbished' | 'used' | 'open_box';
export type OfferStatus = 'active' | 'paused' | 'expired';
export type MediaRole = 'hero' | 'gallery' | 'thumbnail' | 'lifestyle' | 'diagram';

export interface Hub {
  id: string;
  code: string;
  name: string;
  city: string;
  addressLine: string | null;
  region: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface Category {
  id: string;
  code: string;
  slug: string;
  name: string;
  parentId: string | null;
  level: number;
  sortOrder: number;
  iconEmoji: string | null;
  accentColor: string | null;
  heroMediaId: string | null;
  heroUrl: string | null;
  heroMobileMediaId: string | null;
  heroMobileUrl: string | null;
  heroAlt: string | null;
  description: string | null;
  metaTitle: string | null;
  metaDescription: string | null;
  keywords: string[];
  isActive: boolean;
  isVisibleMobile: boolean;
  isFeatured: boolean;
  isSearchable: boolean;
  productsCount: number;
  archivedAt: string | null;
  ogImageMediaId: string | null;
  ogImageUrl: string | null;
  createdAt: string;
  updatedAt: string;
  children?: Category[];
}

export interface MediaAsset {
  id: string;
  kind: 'image' | 'video' | 'document';
  mimeType: string;
  bytes: string;
  width: number | null;
  height: number | null;
  filename: string;
  storageKey: string;
  url: string;
  checksum: string | null;
  altText: string | null;
  uploadedById: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MediaLink {
  id: string;
  mediaId: string;
  productId: string | null;
  variantId: string | null;
  role: MediaRole;
  sortOrder: number;
  url: string | null;
  mimeType: string | null;
  altText: string | null;
}

export interface StockEntry {
  id: string;
  variantId: string;
  hubId: string;
  quantity: number;
  reservedQuantity: number;
  available: number;
  safetyStock: number;
  reorderPoint: number;
  lastMovementAt: string | null;
}

export interface Offer {
  id: string;
  variantId: string;
  condition: OfferCondition;
  price: number;
  compareAtPrice: number | null;
  currency: string;
  preferredHubId: string | null;
  minOrderQty: number;
  maxOrderQty: number | null;
  status: OfferStatus;
  startsAt: string | null;
  endsAt: string | null;
  notes: string | null;
}

export interface ProductVariant {
  id: string;
  productId: string;
  sku: string;
  name: string;
  optionValues: Record<string, string>;
  priceOverride: number | null;
  compareAtPriceOverride: number | null;
  costPrice: number | null;
  weightKg: number | null;
  heightCm: number | null;
  widthCm: number | null;
  depthCm: number | null;
  barcode: string | null;
  isActive: boolean;
  sortOrder: number;
  offers: Offer[];
  stock: StockEntry[];
  mediaLinks: MediaLink[];
}

/** P18 — un pack est un produit qui connaît sa composition. */
export type ProductKind = 'simple' | 'pack';

export interface Product {
  id: string;
  sku: string;
  slug: string;
  title: string;
  kind: ProductKind;
  brand: string | null;
  brandId: string | null;
  brandRef: { id: string; name: string; slug: string } | null;
  categoryId: string;
  status: ProductStatus;
  shortDescription: string | null;
  longDescription: string | null;
  currency: string;
  basePrice: number;
  compareAtPrice: number | null;
  defaultVariantId: string | null;
  attributes: Record<string, unknown>;
  tags: string[];
  metaTitle: string | null;
  metaDescription: string | null;
  publishedAt: string | null;
  aiEnhancedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdById: string | null;
  updatedById: string | null;
  variants: ProductVariant[];
  mediaLinks: MediaLink[];
}

export interface PaginatedProducts {
  items: Product[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ProductsListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: ProductStatus;
  kind?: ProductKind;
  categoryId?: string;
  hubId?: string;
  brandId?: string;
}

/** P18 — vue d'un composant de pack, enrichie prix + disponibilité (écran de composition). */
export interface PackItemView {
  id: string;
  componentVariantId: string;
  productId: string;
  productSlug: string;
  productTitle: string;
  productStatus: string;
  variantName: string;
  variantSku: string;
  variantActive: boolean;
  quantity: number;
  sortOrder: number;
  isHighlighted: boolean;
  unitPrice: number | null;
  subtotal: number | null;
  available: number;
  packsPossible: number;
}

export interface PackPreview {
  items: PackItemView[];
  componentsTotal: number;
  packPrice: number | null;
  savings: number | null;
  availability: number;
  weakestLink: {
    componentVariantId: string;
    productTitle: string;
    packsPossible: number;
  } | null;
  alerts: Array<{ code: string; message: string }>;
}

export interface AddPackItemInput {
  componentVariantId: string;
  quantity?: number;
  sortOrder?: number;
  isHighlighted?: boolean;
}

export interface ProductStats {
  draft: number;
  published: number;
  paused: number;
  archived: number;
  total: number;
}

export interface MediaLinkInput {
  mediaId: string;
  role?: MediaRole;
  sortOrder?: number;
}

// ─── Options & caractéristiques produit (stockées dans product.attributes) ──
// Convention : attributes = { options?: ProductOption[], specs?: ProductSpec[], ... }
// `options` définit les axes de déclinaison (Couleur, Taille…) ; chaque variante
// porte alors optionValues = { [option.name]: value.label }.

export type ProductOptionType = 'text' | 'color';

export interface ProductOptionValue {
  label: string;
  /** Code hex (#RRGGBB) — uniquement pour les options de type couleur. */
  color?: string;
  /** Média associé à cette valeur (couleur) → la photo change au clic côté storefront. */
  mediaId?: string;
}

export interface ProductOption {
  name: string;
  type: ProductOptionType;
  values: ProductOptionValue[];
}

export interface ProductSpec {
  label: string;
  value: string;
}

/** Lit les axes d'options depuis attributes (tolérant : renvoie [] si absent/malformé). */
export function readProductOptions(
  attributes: Record<string, unknown> | null | undefined,
): ProductOption[] {
  const raw = (attributes as { options?: unknown } | null | undefined)?.options;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(
      (o): o is ProductOption =>
        !!o && typeof o === 'object' && typeof (o as ProductOption).name === 'string',
    )
    .map((o) => ({
      name: o.name,
      type: o.type === 'color' ? 'color' : 'text',
      values: Array.isArray(o.values)
        ? o.values
            .filter((v): v is ProductOptionValue => !!v && typeof v.label === 'string')
            .map((v) => ({
              label: v.label,
              ...(typeof v.color === 'string' ? { color: v.color } : {}),
              ...(typeof v.mediaId === 'string' ? { mediaId: v.mediaId } : {}),
            }))
        : [],
    }));
}

/** Lit les caractéristiques depuis attributes (tolérant). */
export function readProductSpecs(
  attributes: Record<string, unknown> | null | undefined,
): ProductSpec[] {
  const raw = (attributes as { specs?: unknown } | null | undefined)?.specs;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (s): s is ProductSpec =>
      !!s &&
      typeof s === 'object' &&
      typeof (s as ProductSpec).label === 'string' &&
      typeof (s as ProductSpec).value === 'string',
  );
}
